import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  useTranslation,
} from 'react-i18next';

import {
  checkLoginIdDuplicate,
} from '../../services/signupService';

import GoogleAddressPicker
  from './GoogleAddressPicker';

import SystemPermissionPreview, {
  getDefaultSignupAuth,
} from './SystemPermissionPreview';


const ID_PASSWORD_REGEX =
  /^[-a-zA-Z0-9!@#$%^&*()_+[\]{};':"\\|,.<>/?`~]{4,12}$/;

const EMAIL_REGEX =
  /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,}$/i;


export default function AdminInformationForm({
  form,
  onChange,
  salesRepOptions,
  isLoadingSalesRep,
  salesAutoMode,
  salesRoleUnavailable,
  isLoginIdDuplicated,
  setIsLoginIdDuplicated,
  onBack,
  onNext,
  isSubmitting,
  setMessage,
}) {
  const {
    t,
  } = useTranslation(
    'app',
  );

  const [
    errors,
    setErrors,
  ] = useState({});

  const [
    touched,
    setTouched,
  ] = useState({});

  const [
    loginIdStatus,
    setLoginIdStatus,
  ] = useState(
    'idle',
  );

  const [
    isAddressPickerOpen,
    setIsAddressPickerOpen,
  ] = useState(
    false,
  );

  const loginIdRequestRef =
    useRef(0);

  const authValue =
    typeof form.auth ===
    'string'
      ? form.auth
      : getDefaultSignupAuth();


  useEffect(
    () => {
      if (
        typeof form.auth !==
        'string'
      ) {
        onChange(
          'auth',
          getDefaultSignupAuth(),
        );
      }
    },
    [
      form.auth,
      onChange,
    ],
  );


  const getFieldError =
    (
      fieldName,
      value,
      currentForm,
    ) => {
      switch (
        fieldName
      ) {
        case 'siteName': {
          const siteName =
            String(
              value ||
              '',
            );

          if (
            !siteName.trim()
          ) {
            return t(
              'signup.validation.companyRequired',
            );
          }

          if (
            siteName.includes(
              ' ',
            )
          ) {
            return t(
              'signup.validation.noSpaces',
            );
          }

          return '';
        }


        case 'loginId': {
          const loginId =
            String(
              value ||
              '',
            );

          if (
            !loginId.trim()
          ) {
            return t(
              'signup.validation.idRequired',
            );
          }

          if (
            !ID_PASSWORD_REGEX.test(
              loginId,
            )
          ) {
            return t(
              'signup.validation.idInvalid',
            );
          }

          if (
            isLoginIdDuplicated
          ) {
            return t(
              'signup.validation.duplicateId',
            );
          }

          return '';
        }


        case 'password': {
          const password =
            String(
              value ||
              '',
            );

          if (
            !password
          ) {
            return t(
              'signup.validation.passwordRequired',
            );
          }

          if (
            !ID_PASSWORD_REGEX.test(
              password,
            )
          ) {
            return t(
              'signup.validation.passwordInvalid',
            );
          }

          return '';
        }


        case 'repassword': {
          const repassword =
            String(
              value ||
              '',
            );

          if (
            !repassword
          ) {
            return t(
              'signup.validation.passwordConfirmRequired',
            );
          }

          if (
            repassword !==
            String(
              currentForm.password ||
              '',
            )
          ) {
            return t(
              'signup.validation.passwordMismatch',
            );
          }

          return '';
        }


        case 'email': {
          const email =
            String(
              value ||
              '',
            );

          if (
            !email.trim()
          ) {
            return t(
              'signup.validation.emailRequired',
            );
          }

          if (
            !EMAIL_REGEX.test(
              email,
            )
          ) {
            return t(
              'signup.validation.emailInvalid',
            );
          }

          return '';
        }


        case 'address': {
          const address =
            String(
              value ||
              '',
            );

          if (
            !address.trim()
          ) {
            return t(
              'signup.validation.addressRequired',
            );
          }

          return '';
        }


        case 'sales': {
          if (
            !value
          ) {
            return t(
              'signup.validation.salesRequired',
            );
          }

          return '';
        }


        default:
          return '';
      }
    };


  const updateFieldError =
    (
      fieldName,
      errorMessage,
    ) => {
      setErrors(
        (
          previous,
        ) => {
          const next = {
            ...previous,
          };

          if (
            errorMessage
          ) {
            next[
              fieldName
            ] =
              errorMessage;
          } else {
            delete next[
              fieldName
            ];
          }

          return next;
        },
      );
    };


  const handleFieldChange =
    (
      fieldName,
      value,
    ) => {
      const nextForm = {
        ...form,

        [
          fieldName
        ]:
          value,
      };

      onChange(
        fieldName,
        value,
      );


      if (
        fieldName ===
        'loginId'
      ) {
        loginIdRequestRef.current +=
          1;

        setLoginIdStatus(
          'idle',
        );

        setIsLoginIdDuplicated(
          false,
        );
      }


      if (
        touched[
          fieldName
        ]
      ) {
        const fieldError =
          getFieldError(
            fieldName,
            value,
            nextForm,
          );

        updateFieldError(
          fieldName,
          fieldError,
        );
      }


      if (
        fieldName ===
          'password' &&
        nextForm.repassword
      ) {
        const repasswordError =
          getFieldError(
            'repassword',
            nextForm.repassword,
            nextForm,
          );

        updateFieldError(
          'repassword',
          repasswordError,
        );
      }


      setMessage?.(
        '',
      );
    };


  const handleFieldBlur =
    (
      fieldName,
    ) => {
      setTouched(
        (
          previous,
        ) => ({
          ...previous,

          [
            fieldName
          ]:
            true,
        }),
      );


      const fieldError =
        getFieldError(
          fieldName,
          form[
            fieldName
          ],
          form,
        );


      updateFieldError(
        fieldName,
        fieldError,
      );
    };


  const handleLoginBlur =
    async () => {
      handleFieldBlur(
        'loginId',
      );


      const loginId =
        String(
          form.loginId ||
          '',
        ).trim();


      if (
        !loginId ||
        !ID_PASSWORD_REGEX.test(
          loginId,
        )
      ) {
        return;
      }


      const requestId =
        ++loginIdRequestRef.current;


      setLoginIdStatus(
        'checking',
      );


      try {
        const duplicated =
          await checkLoginIdDuplicate(
            loginId,
          );


        if (
          requestId !==
          loginIdRequestRef.current
        ) {
          return;
        }


        setIsLoginIdDuplicated(
          duplicated,
        );


        if (
          duplicated
        ) {
          const duplicateMessage =
            t(
              'signup.validation.duplicateId',
            );


          setLoginIdStatus(
            'duplicate',
          );


          updateFieldError(
            'loginId',
            duplicateMessage,
          );


          return;
        }


        setLoginIdStatus(
          'available',
        );


        updateFieldError(
          'loginId',
          '',
        );
      } catch (
        error
      ) {
        console.error(
          '[AdminInformationForm] Failed to check login ID:',
          error,
        );


        if (
          requestId !==
          loginIdRequestRef.current
        ) {
          return;
        }


        setLoginIdStatus(
          'error',
        );


        setIsLoginIdDuplicated(
          false,
        );


        updateFieldError(
          'loginId',
          t(
            'signup.validation.idCheckFailed',
          ),
        );
      }
    };


  useEffect(
    () => {
      const loginId =
        String(
          form.loginId ||
          '',
        ).trim();


      const requestId =
        ++loginIdRequestRef.current;


      if (
        !loginId ||
        !ID_PASSWORD_REGEX.test(
          loginId,
        )
      ) {
        setLoginIdStatus(
          'idle',
        );

        setIsLoginIdDuplicated(
          false,
        );

        return undefined;
      }


      setLoginIdStatus(
        'checking',
      );


      const timer =
        setTimeout(
          async () => {
            try {
              const duplicated =
                await checkLoginIdDuplicate(
                  loginId,
                );


              if (
                requestId !==
                loginIdRequestRef.current
              ) {
                return;
              }


              setIsLoginIdDuplicated(
                duplicated,
              );


              if (
                duplicated
              ) {
                const duplicateMessage =
                  t(
                    'signup.validation.duplicateId',
                  );


                setLoginIdStatus(
                  'duplicate',
                );


                updateFieldError(
                  'loginId',
                  duplicateMessage,
                );


                return;
              }


              setLoginIdStatus(
                'available',
              );


              updateFieldError(
                'loginId',
                '',
              );
            } catch (
              error
            ) {
              console.error(
                '[AdminInformationForm] Failed to check login ID:',
                error,
              );


              if (
                requestId !==
                loginIdRequestRef.current
              ) {
                return;
              }


              setLoginIdStatus(
                'error',
              );


              setIsLoginIdDuplicated(
                false,
              );


              updateFieldError(
                'loginId',
                t(
                  'signup.validation.idCheckFailed',
                ),
              );
            }
          },
          500,
        );


      return () => {
        clearTimeout(
          timer,
        );
      };
    },
    [
      form.loginId,
      setIsLoginIdDuplicated,
      t,
    ],
  );


  const validate =
    () => {
      const nextErrors = {};


      const fieldNames = [
        'siteName',
        'loginId',
        'password',
        'repassword',
        'email',
        'address',
        'sales',
      ];


      fieldNames.forEach(
        (
          fieldName,
        ) => {
          const error =
            getFieldError(
              fieldName,
              form[
                fieldName
              ],
              form,
            );


          if (
            error
          ) {
            nextErrors[
              fieldName
            ] =
              error;
          }
        },
      );


      if (
        isLoginIdDuplicated ||
        loginIdStatus ===
          'duplicate'
      ) {
        nextErrors.loginId =
          t(
            'signup.validation.duplicateId',
          );
      }


      setErrors(
        nextErrors,
      );


      setTouched({
        siteName:
          true,

        loginId:
          true,

        password:
          true,

        repassword:
          true,

        email:
          true,

        address:
          true,

        sales:
          true,
      });


      const firstError =
        Object.values(
          nextErrors,
        )[0];


      if (
        firstError
      ) {
        setMessage?.(
          firstError,
        );
      }


      return (
        Object.keys(
          nextErrors,
        ).length ===
        0
      );
    };


  const handleSubmit =
    async () => {
      setMessage?.(
        '',
      );


      if (
        !validate()
      ) {
        return;
      }


      const loginId =
        String(
          form.loginId ||
          '',
        ).trim();


      setLoginIdStatus(
        'checking',
      );


      try {
        const duplicated =
          await checkLoginIdDuplicate(
            loginId,
          );


        setIsLoginIdDuplicated(
          duplicated,
        );


        if (
          duplicated
        ) {
          const duplicateMessage =
            t(
              'signup.validation.duplicateId',
            );


          setLoginIdStatus(
            'duplicate',
          );


          updateFieldError(
            'loginId',
            duplicateMessage,
          );


          setMessage?.(
            duplicateMessage,
          );


          return;
        }


        setLoginIdStatus(
          'available',
        );


        updateFieldError(
          'loginId',
          '',
        );


        await onNext?.();
      } catch (
        error
      ) {
        console.error(
          '[AdminInformationForm] Failed to check login ID:',
          error,
        );


        const errorMessage =
          t(
            'signup.validation.idCheckFailed',
          );


        setLoginIdStatus(
          'error',
        );


        updateFieldError(
          'loginId',
          errorMessage,
        );


        setMessage?.(
          errorMessage,
        );
      }
    };


  return (
    <section
      className="signup-section"
    >
      <div
        className="signup-section-title"
      >
        <h2>
          {t(
            'signup.information.title',
          )}
        </h2>
      </div>


      <div
        className="signup-information-layout"
      >
        <div
          className="signup-information-column"
        >
          <div
            className="signup-column-title"
          >
            {t(
              'signup.information.memberInformation',
            )}
          </div>


          <div
            className="signup-information-form"
          >
            <SignupField
              label={t(
                'signup.fields.companyName',
              )}
              required
              error={
                touched.siteName
                  ? errors.siteName
                  : ''
              }
            >
              <input
                type="text"
                value={
                  form.siteName
                }
                onChange={(
                  event,
                ) =>
                  handleFieldChange(
                    'siteName',
                    event.target.value,
                  )
                }
                onBlur={
                  () =>
                    handleFieldBlur(
                      'siteName',
                    )
                }
              />
            </SignupField>


            <SignupField
              label={t(
                'signup.fields.userId',
              )}
              required
              error={
                touched.loginId
                  ? errors.loginId
                  : ''
              }
            >
              <input
                type="text"
                value={
                  form.loginId
                }
                autoComplete="off"
                onChange={(
                  event,
                ) =>
                  handleFieldChange(
                    'loginId',
                    event.target.value,
                  )
                }
                onBlur={
                  handleLoginBlur
                }
              />

              {loginIdStatus ===
                'checking' && (
                <span
                  className="signup-field-info"
                >
                  {t(
                    'signup.validation.checkingId',
                  )}
                </span>
              )}

              {loginIdStatus ===
                'available' &&
                !errors.loginId && (
                <span
                  className="signup-field-success"
                >
                  {t(
                    'signup.validation.availableId',
                  )}
                </span>
              )}
            </SignupField>


            <SignupField
              label={t(
                'signup.fields.password',
              )}
              required
              error={
                touched.password
                  ? errors.password
                  : ''
              }
            >
              <input
                type="password"
                value={
                  form.password
                }
                autoComplete="new-password"
                onChange={(
                  event,
                ) =>
                  handleFieldChange(
                    'password',
                    event.target.value,
                  )
                }
                onBlur={
                  () =>
                    handleFieldBlur(
                      'password',
                    )
                }
              />
            </SignupField>


            <SignupField
              label={t(
                'signup.fields.passwordConfirm',
              )}
              required
              error={
                touched.repassword
                  ? errors.repassword
                  : ''
              }
            >
              <input
                type="password"
                value={
                  form.repassword
                }
                autoComplete="new-password"
                onChange={(
                  event,
                ) =>
                  handleFieldChange(
                    'repassword',
                    event.target.value,
                  )
                }
                onBlur={
                  () =>
                    handleFieldBlur(
                      'repassword',
                    )
                }
              />
            </SignupField>


            <SignupField
              label={t(
                'signup.fields.email',
              )}
              required
              error={
                touched.email
                  ? errors.email
                  : ''
              }
            >
              <input
                type="email"
                value={
                  form.email
                }
                onChange={(
                  event,
                ) =>
                  handleFieldChange(
                    'email',
                    event.target.value,
                  )
                }
                onBlur={
                  () =>
                    handleFieldBlur(
                      'email',
                    )
                }
              />
            </SignupField>


            <SignupField
              label={t(
                'signup.fields.address',
              )}
              required
              error={
                touched.address
                  ? errors.address
                  : ''
              }
            >
              <div
                className="signup-address-input-row"
              >
                <input
                  type="text"
                  readOnly
                  aria-readonly="true"
                  onClick={() => setIsAddressPickerOpen(true)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setIsAddressPickerOpen(true);
                    }
                  }}
                  value={
                    form.address
                  }
                  onBlur={
                    () =>
                      handleFieldBlur(
                        'address',
                      )
                  }
                />

                <button
                  type="button"
                  className="signup-map-button"
                  onClick={
                    () =>
                      setIsAddressPickerOpen(
                        true,
                      )
                  }
                >
                  {t(
                    'signup.addressPicker.open',
                  )}
                </button>
              </div>
            </SignupField>


            {salesAutoMode ? (
              salesRoleUnavailable ? (
                <div className="signup-sales-auto" role="alert">
                  {t('signup.validation.regionalSalesUnavailable', { defaultValue: 'The sales account for this country is not configured. Contact the portal administrator.' })}
                </div>
              ) : null
            ) : <SignupField
              label={t(
                'signup.fields.salesRepresentative',
              )}
              required
              error={
                touched.sales
                  ? errors.sales
                  : ''
              }
            >
              <select
                value={
                  form.sales
                }
                disabled={
                  isLoadingSalesRep
                }
                onChange={(
                  event,
                ) =>
                  handleFieldChange(
                    'sales',
                    event.target.value,
                  )
                }
                onBlur={
                  () =>
                    handleFieldBlur(
                      'sales',
                    )
                }
              >
                <option
                  value=""
                >
                  {isLoadingSalesRep
                    ? t(
                        'signup.fields.loadingSalesRepresentative',
                      )
                    : t(
                        'signup.fields.selectSalesRepresentative',
                      )}
                </option>

                {salesRepOptions.map(
                  (
                    option,
                  ) => (
                    <option
                      key={
                        option.value
                      }
                      value={
                        option.value
                      }
                    >
                      {
                        option.label
                      }
                    </option>
                  ),
                )}
              </select>
            </SignupField>}
          </div>
        </div>


        <div
          className="signup-information-column signup-permission-column"
        >
          <SystemPermissionPreview
            value={
              authValue
            }
            onChange={(
              nextAuth,
            ) =>
              onChange(
                'auth',
                nextAuth,
              )
            }
          />
        </div>
      </div>


      <div
        className="signup-form-actions"
      >
        <button
          type="button"
          className="signup-secondary-button"
          onClick={
            onBack
          }
          disabled={
            isSubmitting
          }
        >
          {t(
            'common.previous',
          )}
        </button>

        <button
          type="button"
          className="signup-primary-button"
          onClick={
            handleSubmit
          }
          disabled={
            isSubmitting ||
            loginIdStatus ===
              'checking'
          }
        >
          {isSubmitting
            ? t(
                'signup.submitting',
              )
            : t(
                'signup.submit',
              )}
        </button>
      </div>


      <GoogleAddressPicker
        open={
          isAddressPickerOpen
        }
        initialAddress={
          form.address
        }
        onClose={
          () =>
            setIsAddressPickerOpen(
              false,
            )
        }
        onSelect={(
          mapInfo,
        ) => {
          handleFieldChange(
            'address',
            mapInfo.address ||
            '',
          );

          onChange(
            'country',
            mapInfo.country ||
            '',
          );

          onChange(
            'position',
            mapInfo.position ||
            null,
          );

          onChange(
            'placeID',
            mapInfo.placeID ||
            '',
          );

          setIsAddressPickerOpen(
            false,
          );
        }}
      />
    </section>
  );
}


function SignupField({
  label,
  required,
  error,
  children,
}) {
  return (
    <div
      className={
        error
          ? 'signup-field has-error'
          : 'signup-field'
      }
    >
      <label
        className="signup-field-label"
      >
        {
          label
        }

        {required && (
          <span
            className="signup-required"
          >
            *
          </span>
        )}
      </label>

      {
        children
      }

      {error && (
        <span
          className="signup-field-error"
        >
          {
            error
          }
        </span>
      )}
    </div>
  );
}
